import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function Footer() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  return (
    <footer
      className={`relative border-t ${
        isLight
          ? "bg-[#f0f0f5] border-t border-black/10"
          : "bg-cosmic-900 border-t border-white/5"
      }`}
    >
      {/* CTA Section */}
      <div className="relative py-20 sm:py-28">
        <div className="mx-auto max-w-2xl text-center px-6 sm:px-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {t("footer.cta_title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`mt-6 text-base sm:text-lg leading-relaxed ${
              isLight ? "text-[#6e6e73]" : "text-silver-500"
            }`}
          >
            {t("footer.cta_subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8"
          >
            <Link
              to="/experience"
              className={`inline-block rounded-full px-10 py-3.5 text-base font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isLight
                  ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]"
                  : "bg-gold-500 text-cosmic-950 hover:bg-gold-400"
              }`}
            >
              {t("footer.cta_button")}
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="relative z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-6 pb-2">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20 lg:gap-24">
            {/* Brand */}
            <div className="flex flex-col items-center md:items-start gap-5 md:max-w-[320px]">
              <div className="flex items-center gap-2.5">
                <img
                  src={isLight
                    ? "/images/home/5ca4f114-ceca-45cb-82ea-08e6275d96a2.png"
                    : "/images/home/adaptive-icon-foreground.png"
                  }
                  alt={t("about.brand")}
                  className="w-8 h-8 object-contain"
                />
                <span
                  className={`text-xl font-bold tracking-tight ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {t("about.brand")}
                </span>
              </div>
              <p
                className={`text-sm text-center md:text-left leading-relaxed ${
                  isLight ? "text-[#6e6e73]/70" : "text-silver-500/70"
                }`}
              >
                {t("footer.brand_slogan")}
              </p>
            </div>

            {/* Links */}
            <div className="flex gap-16 justify-center md:ml-auto">
              <div className="flex flex-col items-center md:items-end gap-4">
                <h4
                  className={`text-sm font-semibold ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {t("footer.about")}
                </h4>
                <nav className="flex flex-col items-center md:items-end gap-3">
                  <Link
                    to="/about"
                    className={`text-sm transition-colors duration-150 cursor-pointer ${
                      isLight
                        ? "text-[#6e6e73]/70 hover:text-[#1c1c1e]"
                        : "text-silver-500/70 hover:text-white"
                    }`}
                  >
                    {t("footer.about_us")}
                  </Link>
                  <Link
                    to="/contact"
                    className={`text-sm transition-colors duration-150 cursor-pointer ${
                      isLight
                        ? "text-[#6e6e73]/70 hover:text-[#1c1c1e]"
                        : "text-silver-500/70 hover:text-white"
                    }`}
                  >
                    {t("footer.contact_us")}
                  </Link>
                </nav>
              </div>
              <div className="flex flex-col items-center md:items-end gap-4">
                <h4
                  className={`text-sm font-semibold ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {t("footer.legal")}
                </h4>
                <nav className="flex flex-col items-center md:items-end gap-3">
                  <Link
                    to="/privacy"
                    className={`text-sm transition-colors duration-150 cursor-pointer ${
                      isLight
                        ? "text-[#6e6e73]/70 hover:text-[#1c1c1e]"
                        : "text-silver-500/70 hover:text-white"
                    }`}
                  >
                    {t("footer.privacy")}
                  </Link>
                  <Link
                    to="/terms"
                    className={`text-sm transition-colors duration-150 cursor-pointer ${
                      isLight
                        ? "text-[#6e6e73]/70 hover:text-[#1c1c1e]"
                        : "text-silver-500/70 hover:text-white"
                    }`}
                  >
                    {t("footer.terms")}
                  </Link>
                </nav>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 mb-2 text-center">
            <p
              className={`text-xs leading-relaxed max-w-3xl mx-auto ${
                isLight ? "text-[#6e6e73]/50" : "text-silver-600/50"
              }`}
            >
              內容資訊僅供娛樂使用，不等於專業測評，不代表價值評判，無任何現實指導意義，僅供娛樂參考
            </p>
          </div>

          {/* Copyright */}
          <div
            className={`mt-6 pt-4 border-t ${
              isLight ? "border-black/10" : "border-white/10"
            }`}
          >
            <div
              className={`flex flex-col items-center gap-3 text-xs ${
                isLight ? "text-[#6e6e73]/40" : "text-silver-500/40"
              }`}
            >
              <span>
                {t("footer.copyright", {
                  year: new Date().getFullYear(),
                  brand: t("about.brand"),
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}